/**
 * The List — what we're committed to, and the pool behind it.
 * Committed = pinned membership (durable). Order = collection sort_order.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, Plus, RefreshCw, X } from 'lucide-react';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { FOCUS_COLLECTION_NAME, useFocusActions, useFocusList } from './useFocusList';

export default function FocusListScreen() {
  const app = getAppDefinition('focus')!;
  const navigate = useNavigate();
  const { collectionId } = useParams<{ collectionId: string }>();
  const { loading, focusLists, collection, committed, pool, candidates, userId } =
    useFocusList(collectionId ?? null);
  const actions = useFocusActions();

  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  // No list selected yet: land on the first one, or offer to start one.
  const firstListId = focusLists[0]?.id;
  const visibleCandidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return candidates.filter((i) => !q || i.title.toLowerCase().includes(q)).slice(0, 40);
  }, [candidates, query]);

  const startList = async () => {
    setBusy(true);
    const created = await actions.createCollection(
      FOCUS_COLLECTION_NAME,
      'The few things this team is committed to right now.',
      userId ?? undefined,
    );
    setBusy(false);
    if (created) navigate(`/apps/focus/${created.id}`, { replace: true });
  };

  const move = async (itemId: string, direction: -1 | 1) => {
    if (!collection) return;
    const ordered = [...committed, ...pool].map((e) => e.item.id);
    const from = ordered.indexOf(itemId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ordered.length) return;
    [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
    await actions.reorderItems(collection.id, ordered);
  };

  if (!loading && focusLists.length === 0) {
    return (
      <AppShell app={app}>
        <ScreenHeading
          title="Focus"
          subtitle="The few things this team is committed to right now — and everything waiting behind them."
        />
        <EmptyState
          title="Start your focus list"
          hint="One list, the few things on it pinned, everything else in the pool behind."
          action={
            <PrimaryButton onClick={() => void startList()} disabled={busy}>
              <Plus className="h-4 w-4" /> {busy ? 'Creating…' : 'Start the list'}
            </PrimaryButton>
          }
        />
      </AppShell>
    );
  }

  if (!collectionId && firstListId) {
    return <Navigateish to={`/apps/focus/${firstListId}`} />;
  }

  if (!collection) {
    return (
      <AppShell app={app}>
        <EmptyState title="Loading your focus list…" />
      </AppShell>
    );
  }

  return (
    <AppShell
      app={app}
      actions={
        <SecondaryButton onClick={() => navigate(`/apps/focus/${collection.id}/refocus`)}>
          <RefreshCw className="h-4 w-4" /> Re-focus
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title={collection.name}
        subtitle="Pinned work sits above the line. Everything else is the pool behind it."
      />
      <BallLine
        ballWith={
          committed.length === 0
            ? 'you — pin the few that matter'
            : 'the team — this is what we said matters'
        }
      />

      <div className="space-y-4">
        <AppCard className="!p-0">
          <div className="flex items-center justify-between px-5 pt-5">
            <Eyebrow>Pinned · {committed.length}</Eyebrow>
            {committed.length > 0 ? <StatusChip label="This period" tone="mustard" /> : null}
          </div>
          {committed.length === 0 ? (
            <p className="px-5 pb-5 pt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              Nothing pinned yet — pin an item from the pool below.
            </p>
          ) : (
            <ol className="mt-2">
              {committed.map((entry, i) => (
                <li
                  key={entry.item.id}
                  className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-electric-mustard-100 font-mono text-xs text-electric-mustard-800 dark:bg-electric-mustard-900/40 dark:text-electric-mustard-300">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                    {entry.item.title}
                  </span>
                  <button
                    type="button"
                    aria-label="Move up"
                    onClick={() => void move(entry.item.id, -1)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    onClick={() => void move(entry.item.id, 1)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void actions.uncommit(collection.id, entry.item.id)}
                    className="rounded px-2 py-1 font-body text-xs text-gray-500 hover:bg-gray-100 dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
                  >
                    Unpin
                  </button>
                </li>
              ))}
            </ol>
          )}
        </AppCard>

        <AppCard className="!p-0">
          <div className="flex items-center justify-between px-5 pt-5">
            <Eyebrow>The pool · {pool.length}</Eyebrow>
            <SecondaryButton onClick={() => setAdding((v) => !v)} className="!px-3 !py-1.5">
              <Plus className="h-3.5 w-3.5" /> Add
            </SecondaryButton>
          </div>
          {adding ? (
            <div className="border-b border-gray-100 px-5 py-3 dark:border-[var(--rule)]">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search items to add…"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
              <div className="mt-2 max-h-56 overflow-y-auto">
                {visibleCandidates.length === 0 ? (
                  <p className="py-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    Nothing left to add — capture more in the Items app.
                  </p>
                ) : (
                  visibleCandidates.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => void actions.add(collection.id, item.id)}
                      className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                    >
                      <Plus className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                      <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-800 dark:text-[var(--text-pri)]">
                        {item.title}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}
          {pool.length === 0 ? (
            <p className="px-5 pb-5 pt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
              The pool is empty.
            </p>
          ) : (
            <ul className="mt-2">
              {pool.map((entry) => (
                <li
                  key={entry.item.id}
                  className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                >
                  <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                    {entry.item.title}
                  </span>
                  <button
                    type="button"
                    onClick={() => void actions.commit(collection.id, entry.item.id)}
                    className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 font-body text-xs font-medium text-white hover:bg-primary-700"
                  >
                    <Check className="h-3 w-3" /> Pin
                  </button>
                  <button
                    type="button"
                    aria-label="Remove from list"
                    onClick={() => void actions.remove(collection.id, entry.item.id)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </AppCard>
      </div>
    </AppShell>
  );
}

/** Redirect helper — navigation is an effect, never render-phase work. */
function Navigateish({ to }: { to: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [navigate, to]);
  return null;
}
